import type React from 'react';

import { Stack } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { type ReactElement } from 'react';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useFormProviderKnowMePropsContext } from '@/components/form/FormProviderKnowMe.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { isFormFieldRequired } from '@/utils/form/isFormFieldRequired.util.ts';
import { scrollFocusRef } from '@/utils/form/scrollFocusRef.util.ts';

import { type FormPropsModel } from './model/FormProps.model';
import { TextError } from './TextError.comp';

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends FormPropsModel<TName> {
  beforeLabelComponent?: React.ReactNode;
  EndIcon?: React.ReactNode;
  isInputHighlighted?: boolean;
  maxLength?: number;
  maxRows?: number;
  minRows?: number;
  multiline?: boolean;
  nextToLabelComponent?: React.ReactNode;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  StartIcon?: React.ReactNode;
  type?: React.InputHTMLAttributes<unknown>['type'];
}

export const TextFormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>,
): ReactElement<Props<TFieldValues, TName>> => {
  const { control } = useFormContext<TFieldValues>();
  const {
    beforeLabelComponent = null,
    EndIcon,
    fullWidth,
    isInputHighlighted,
    label,
    maxLength,
    maxRows,
    minRows,
    name,
    nextToLabelComponent = null,
    onChange,
    placeholder,
    StartIcon,
    ...rest
  } = props;
  const { validation } = useFormProviderKnowMePropsContext();
  const render: ControllerProps<TFieldValues, TName>['render'] = ({
    field,
    fieldState: { error },
  }) => {
    const { ref, ...fieldRest } = field;
    const isRequired = isFormFieldRequired(field.name, validation);

    return (
      <FormControl fullWidth={fullWidth ?? true} ref={scrollFocusRef(ref)}>
        <TextField
          {...fieldRest}
          value={field.value ?? ''}
          {...rest}
          error={Boolean(error)}
          hiddenLabel={!label}
          label={
            <Stack direction="row" sx={{ alignItems: 'center' }}>
              {beforeLabelComponent}
              {label}
              {nextToLabelComponent}
            </Stack>
          }
          maxRows={maxRows ?? (minRows ?? 0) + 2}
          minRows={minRows}
          onChange={event => {
            if (onChange) {
              onChange(event);
            } else {
              field.onChange(event);
            }
          }}
          placeholder={placeholder ?? String(label)}
          size={'small'}
          slotProps={{
            input: {
              endAdornment: !EndIcon ? undefined : (
                <InputAdornment position="end">{EndIcon}</InputAdornment>
              ),
              startAdornment: !StartIcon ? undefined : (
                <InputAdornment position="start">{StartIcon}</InputAdornment>
              ),
              sx: isInputHighlighted
                ? {
                    border: `1px solid ${themeColors.turquoise}`,
                  }
                : undefined,
            },
            inputLabel: {
              disableAnimation: false,
              required: isRequired,
              shrink: true,
              sx: {
                ...(rest.sx ?? {}),
                fieldset: {
                  background: 'none',
                },
              },
            },
          }}
          variant="standard"
        />
        <TextError error={error} />
      </FormControl>
    );
  };

  return (
    <Controller<TFieldValues, TName>
      control={control}
      name={name}
      render={render}
      rules={{
        maxLength,
      }}
    />
  );
};
